import '@dp-watson-web/components/dist/watson.css';
import './styles/main.scss';

import { VueQueryPlugin } from '@tanstack/vue-query';
import { createApp } from 'vue';

import type { Locale } from '@/config/build';
import { loadConfig } from '@/config/utilities/loader';
import { createLoginFlow } from '@/plugins/login-flow/index.js';
import { createModalPlugin } from '@/plugins/modal/index';
import { createRequest } from '@/plugins/request/index';
import type { Environment } from '@/plugins/request/types/index';
import { createToastMessagesPlugin } from '@/plugins/toast/index';
import { APP_CONFIG_KEY, LOCALE_CONFIG_KEY } from '@/providers';
import { RouteNames } from '@/router/routeNames';
import { useNoaIntegrationAuthService } from '@/shared/services';

import App from './App.vue';
import { createAvoPlugin } from './plugins/avo/plugin';
import { LOGIN_ROUTE, router } from './router';
import { useRouterAclGuard } from './shared/layouts/base';

declare const noaHubConfig: {
  locale: Locale;
  environment: Environment;
  testingName: string;
};

async function bootstrap({ locale, environment, testingName }: typeof noaHubConfig) {
  const app = createApp(App);

  const appConfig = await loadConfig({
    locale,
    environment,
    testingName,
  });

  const { setupRemoteJs } = await import('./setup/setup-remotejs');

  setupRemoteJs(environment, testingName);

  const { setupPinia } = await import('./setup/setup-pinia');

  setupPinia(app);

  router.beforeEach(useRouterAclGuard);

  const loginFlow = createLoginFlow({
    router,
    route: LOGIN_ROUTE(),
    service: () =>
      useNoaIntegrationAuthService({
        ...appConfig.auth,
      }),
    options: {
      baseRoute: RouteNames.HOME,
    },
  });

  const requestPlugin = createRequest({
    noa_integrations: {
      token: loginFlow.token,
    },
    globalConfig: {
      locale: locale,
      env: environment,
      testingName: testingName,
    },
  });

  const modalPlugin = createModalPlugin();
  const toastPlugin = createToastMessagesPlugin();

  const avoPlugin = createAvoPlugin({
    env: environment,
    locale: locale,
  });

  app.use(router);
  app.use(requestPlugin);
  app.use(modalPlugin);
  app.use(toastPlugin);
  app.use(VueQueryPlugin);
  app.use(loginFlow);
  app.use(avoPlugin);

  const { setupI18n } = await import('./setup/setup-i18n');

  await setupI18n(app, locale);

  const { setupSentry } = await import('./setup/setup-sentry');

  await setupSentry(app, locale, environment, testingName);

  app.provide(APP_CONFIG_KEY, appConfig);
  app.provide(LOCALE_CONFIG_KEY, locale);

  await router.isReady();

  app.mount('#app');
}

bootstrap(noaHubConfig);
